/**
 * The truck device's own identity, held on the tablet.
 *
 * A device is paired to one truck, once, with a code from the office. What it
 * keeps afterwards is a long-lived secret that says "I am the screen in truck
 * ABC-123" — and that secret, not anything the app sends, is what the server
 * reads the truck off. It therefore outlives every driver session on the device:
 * shifts change, drivers sign in and out, and the pairing is untouched.
 *
 * Two separate things live here, and keeping them separate is the whole point:
 *
 *   the pairing   the device token + the truck it names. Cleared only by the
 *                 office revoking the device, or by re-pairing.
 *   the shift     the signed-in driver's JWT, which lives in `token` alongside
 *                 every other login in the app. Cleared on sign-out, on
 *                 displacement by another driver, and on the idle timeout.
 *
 * Signing out must never clear the pairing, or every handover between drivers
 * would need an admin and a fresh code.
 */

const TOKEN_KEY = "deviceToken";
const TRUCK_KEY = "deviceTruck";
const SESSION_KEY = "deviceSession";

import type { PairedTruck } from "../types/devices";

export type { PairedTruck };

export interface DevicePairing {
  deviceToken: string;
  truck: PairedTruck;
  label?: string;
}

/**
 * Every read is wrapped, because `localStorage` throws rather than returning
 * null in a private window and in a browser set to block site data — and a
 * tablet in a cab is exactly the kind of device somebody has locked down. A
 * device that cannot remember its pairing should show the pairing screen, not a
 * white page.
 */
const read = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* A device that cannot persist is usable for this shift and will ask to be
       paired again next time. Better than refusing to work at all. */
  }
};

const drop = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
};

/** The device secret sent on every request from this tablet, or null. */
export const getDeviceToken = (): string | null => read(TOKEN_KEY);

/** Which truck this screen is the screen for — shown above the keypad. */
export const getPairedTruck = (): PairedTruck | null => {
  const raw = read(TRUCK_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed?._id ? parsed : null;
  } catch {
    // Corrupt stored value: treat as unpaired rather than crashing the keypad.
    return null;
  }
};

/**
 * Whether this browser is a truck's screen.
 *
 * A fact about the *device*, not about who is signed in on it. True on the
 * tablet in the cab from the moment it is paired until it is revoked — including
 * while nobody is signed in, and including while somebody is signed in with a
 * password.
 */
export const isPaired = (): boolean => Boolean(getDeviceToken());

/**
 * Whether the *current login* is a truck session.
 *
 * The distinction between this and `isPaired` is the one the whole driver UI
 * turns on, and conflating the two is easy and wrong. A driver can perfectly
 * well sign in with their email and password on the truck's own tablet: the
 * device is paired, so `isPaired()` is true, but the session they are holding
 * carries no device claim and the server will refuse every container endpoint
 * to it.
 *
 * Only that second question decides what a driver is shown. Containers belong to
 * a truck and are reached by signing in on the truck's screen with a PIN — a
 * password session, wherever it is running, has no truck and must not be offered
 * the work, the shift controls, or the pairing screen.
 *
 * The marker is written by the PIN login and cleared by every path that ends a
 * driver session or starts a different one, so it can only be true while a real
 * device session is in hand. It is a UI hint and nothing more: the server checks
 * the token's device claim against the device's own record on every request, so
 * a forged marker buys a driver nothing but a screen full of refusals.
 */
export const hasDeviceSession = (): boolean =>
  Boolean(getDeviceToken()) && read(SESSION_KEY) === "1";

/** Record that the session just opened is a truck session. */
export const markDeviceSession = () => write(SESSION_KEY, "1");

/** Store what the pairing call returned. Called once per tablet, ever. */
export const savePairing = ({ deviceToken, truck }: DevicePairing) => {
  write(TOKEN_KEY, deviceToken);
  write(TRUCK_KEY, JSON.stringify(truck));
};

/**
 * Refresh the cached truck from whatever the server just said about it.
 *
 * The pairing is a one-time act, so `savePairing` runs once in a tablet's life
 * and everything downstream reads the snapshot it wrote — which is fine for the
 * *identity* of the truck and wrong for its *details*. A plate is edited in the
 * office and the screen in the cab goes on showing the old one, for months,
 * because nothing ever writes here again.
 *
 * So every response that carries the truck refreshes it: the keypad's context
 * call, the PIN sign-in, and the container list the driver is looking at. None
 * of them is an extra request — the truck is already in all three.
 *
 * Merged rather than replaced, because those three carry different amounts of
 * it. The container list knows only the plate and display name (that is all its
 * populate selects), and letting it blank the model would trade one stale field
 * for one missing one.
 *
 * The `_id` is the identity and is never taken from the argument: a payload that
 * could repoint the cached pairing at another vehicle is a payload that could
 * mislabel which truck a driver thinks they are standing in.
 */
export const updatePairedTruck = (truck: Partial<PairedTruck> | null | undefined) => {
  if (!truck) return;

  const current = getPairedTruck();
  if (!current) return;

  const next: PairedTruck = {
    ...current,
    ...(truck.registrationNumber
      ? { registrationNumber: truck.registrationNumber }
      : {}),
    ...(truck.displayName !== undefined
      ? { displayName: truck.displayName }
      : {}),
    ...(truck.markAndModel !== undefined
      ? { markAndModel: truck.markAndModel }
      : {}),
    _id: current._id,
  };

  // Only touch storage when something actually moved — this runs on every
  // container-list load, and a write per poll for an unchanged value is noise.
  if (
    next.registrationNumber === current.registrationNumber &&
    next.displayName === current.displayName &&
    next.markAndModel === current.markAndModel
  ) {
    return;
  }

  write(TRUCK_KEY, JSON.stringify(next));
};

/**
 * Who wants to know the moment this tablet stops being a truck's screen.
 *
 * The pairing is dropped by the axios interceptor, which is a long way from the
 * keypad that draws it — and `localStorage` fires no event for writes made by
 * the same document, so the screen had no way to find out. The result was the
 * one the yard actually reported: an admin generates a new code, the driver
 * types their PIN, and the tablet answers "this device is no longer paired"
 * *underneath a keypad that is still sitting there*, with the pairing already
 * gone from storage and the pairing form one unprompted page-reload away.
 *
 * A set of callbacks rather than a store or a context, because exactly one
 * screen cares and it needs to hear this from code that has no React in scope.
 */
const pairingListeners = new Set<() => void>();

/**
 * Be told when the pairing is forgotten. Returns its own unsubscribe, so an
 * effect can hand it straight back as cleanup.
 */
export const onPairingCleared = (listener: () => void) => {
  pairingListeners.add(listener);
  return () => {
    pairingListeners.delete(listener);
  };
};

/**
 * Forget the pairing entirely — the device is no longer this truck's.
 *
 * Only for a device the server has told us it no longer recognises (revoked,
 * re-paired elsewhere, the truck deleted). Never on sign-out: see the note at
 * the top.
 */
export const clearPairing = () => {
  drop(TOKEN_KEY);
  drop(TRUCK_KEY);
  // A device that is no longer this truck's cannot be holding a session on it.
  drop(SESSION_KEY);

  // Storage is the record; this is what makes the screen agree with it. A
  // listener that throws must not stop the others from being told.
  for (const listener of pairingListeners) {
    try {
      listener();
    } catch {
      /* ignore */
    }
  }
};

/**
 * End the shift locally — the driver's credentials go, the pairing stays.
 *
 * Shared by the sign-out button and by the interceptor's handling of a session
 * the server has ended, so a driver displaced by a colleague and a driver who
 * tapped the button land on the same keypad in the same state.
 */
export const clearDriverSession = () => {
  try {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  } catch {
    /* ignore */
  }
  // The marker goes with the credentials it described. Left behind, it would
  // claim a truck session over whatever signs in next — including a password
  // login on this same tablet, which is precisely the case it exists to exclude.
  drop(SESSION_KEY);
};

/** Where a device with no live driver belongs. */
export const DEVICE_ROUTE = "/truck";

/**
 * The one server answer that means this tablet's pairing is genuinely worthless.
 *
 * Named here because two places have to agree on it — the axios interceptor and
 * the keypad — and because forgetting a pairing is destructive: it costs a trip
 * to the office for a fresh code. Nothing else may trigger it. A 404 from a
 * disabled module, a timeout, a flat network on a cold boot: all of those leave
 * the pairing exactly where it is.
 */
export const DEVICE_UNPAIRED = "DEVICE_UNPAIRED";
